import { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import { AuthContext } from "App";
import * as api from "apis/FusionAPI";
import Cookies from "js-cookie";

let eventSource;
let isConnectingOrConnected = false;
let sData = {};

export const usePublicData = () => {
  const { paramValue } = useParams();
  const auth = useContext(AuthContext);

  const [data, setData] = useState({});
  const [userDevicesOriginal, setUserDevicesOriginal] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);

  const [calcCenter, setCalcCenter] = useState({
    lat: 29.8105572,
    lng: -98.2212979,
  });
  const [calcZoom, setCalcZoom] = useState(11);

  const deviceSortCategories = {
    "Road Closures": [
      "Manual Road Closure",
      "Technology Bridge_Flashing Lights",
    ],
    Sirens: ["Technology Bridge_Siren/Giant Voice"],
  };

  const deviceTypeToCategory = (device_type) => {
    return Object.keys(deviceSortCategories).find((category) =>
      deviceSortCategories[category].includes(device_type)
    );
  };

  const getUserDevices = async () => {
    if (!auth?.auth?.email) return;
    console.log("Getting userDevices");
    const res = await api.getSubscribedDevices();
    console.log("res in getUserDevices", res);
    if (res.status === 200) {
      setSelectedDevices(res.data.subscriptions);
      setUserDevicesOriginal(res.data.subscriptions);
    }
  };

  const groupDevicesByCategoryAndGroup = (
    devicesSortedByCounty,
    deviceSortCategories
  ) => {
    const categorizedGroups = {};

    // Initialize categories
    Object.keys(deviceSortCategories).forEach((category) => {
      categorizedGroups[category] = {};
    });

    // Helper to get category for a device type
    const getCategory = (device_type) =>
      Object.keys(deviceSortCategories).find((cat) =>
        deviceSortCategories[cat].includes(device_type)
      );

    // Process each county
    Object.values(devicesSortedByCounty).forEach((countyDevices) => {
      Object.values(countyDevices).forEach((device) => {
        const category = getCategory(device.device_type);
        if (!category) return;

        // If device has group_Data, add to each group
        if (device.group_Data && device.group_Data.length > 0) {
          device.group_Data.forEach((group) => {
            if (!categorizedGroups[category][group.id]) {
              categorizedGroups[category][group.id] = {
                ...group,
                devices: [],
                // Calculate center of group from coordinates if available
                center:
                  group.coordinates && group.coordinates.length > 0
                    ? calculateGroupCenter(group.coordinates)
                    : null,
              };
            }
            categorizedGroups[category][group.id].devices.push(device);
          });
        } else {
          // No group, treat device as its own group with alias as name
          const groupId = `ungrouped-${device.id}`;
          const deviceLocation = JSON.parse(device.location);
          categorizedGroups[category][groupId] = {
            id: groupId,
            name: device.alias,
            devices: [device],
            center: deviceLocation,
          };
        }
      });
    });

    // Convert group objects to arrays for each category
    const result = {};
    Object.keys(categorizedGroups).forEach((category) => {
      result[category] = Object.values(categorizedGroups[category]).map(
        (group) => {
          // If group doesn't have a center, calculate from devices
          if (!group.center && group.devices.length > 0) {
            group.center = {
              lat:
                group.devices.reduce(
                  (acc, device) => acc + JSON.parse(device.location).lat,
                  0
                ) / group.devices.length,
              lng:
                group.devices.reduce(
                  (acc, device) => acc + JSON.parse(device.location).lng,
                  0
                ) / group.devices.length,
            };
          }
          return group;
        }
      );
    });

    return result;
  };

  // Helper function to calculate center from coordinates
  const calculateGroupCenter = (coordinates) => {
    if (!coordinates || coordinates.length === 0) return null;

    const allCoords = coordinates.flatMap((coord) => coord.coordinates || []);
    if (allCoords.length === 0) return null;

    const avgLat =
      allCoords.reduce((sum, coord) => sum + coord.lat, 0) / allCoords.length;
    const avgLng =
      allCoords.reduce((sum, coord) => sum + coord.lng, 0) / allCoords.length;

    return { lat: avgLat, lng: avgLng };
  };

  const groupedDevices = groupDevicesByCategoryAndGroup(
    data,
    deviceSortCategories
  );

  // Effect for auth changes
  useEffect(() => {
    console.log("Auth changed", auth);
    getUserDevices();
  }, [auth]);

  // Effect for setting up event source
  useEffect(() => {
    navigator.geolocation.getCurrentPosition((position) => {
      Cookies.set("lat", position.coords.latitude);
      Cookies.set("lng", position.coords.longitude);
    });

    if (!eventSource && !isConnectingOrConnected) {
      (async () => {
        isConnectingOrConnected = true;
        eventSource = await api.GetPublicDeviceInfo(paramValue);

        eventSource.addEventListener("initialize", (event) => {
          const _data = JSON.parse(event.data);
          console.log("RAW DEVICE DATA FROM API", _data);
          setData(_data);
          sData = _data;
        });

        eventSource.addEventListener("nodeUpdated", (event) => {
          const _data = JSON.parse(event.data);
          console.log("nodeUpdated", _data);

          // Clone current state data
          const newData = { ...sData };

          // Search through all counties to find which one contains this device
          let deviceFound = false;
          Object.keys(newData).forEach((county) => {
            if (newData[county] && newData[county][_data.id]) {
              // Found the device in this county, update it
              newData[county][_data.id] = _data;
              deviceFound = true;
            }
          });

          // If device wasn't found in any county
          if (!deviceFound) {
            // If the device is new, add it to an "Unassigned" county
            if (!newData["Unassigned"]) {
              newData["Unassigned"] = {};
            }
            newData["Unassigned"][_data.id] = _data;
          }

          setData(newData);
          sData = newData;
        });

        eventSource.onopen = (event) => {};
        eventSource.onerror = (event) => {
          eventSource = null;
          isConnectingOrConnected = false;
        };
        eventSource.onclose = (event) => {
          eventSource = null;
          isConnectingOrConnected = false;
        };
      })();

      return () => {
        if (eventSource) {
          eventSource.close();
          eventSource = null;
          isConnectingOrConnected = false;
        }
      };
    }
  }, []);

  return {
    data,
    userDevicesOriginal,
    selectedDevices,
    setSelectedDevices,
    setUserDevicesOriginal,
    calcCenter,
    setCalcCenter,
    calcZoom,
    setCalcZoom,
    deviceTypeToCategory,
    groupedDevices,
    getUserDevices,
  };
};
